import { useEffect, useState } from 'react';
import type { ResolvedComponent } from '@inertiajs/react';
import { usePage } from '@inertiajs/react';
import { ContactForm } from '@/components/contact/ContactForm';
import { PageHeader } from '@/components/ui/PageHeader';
import { RevealOnScroll } from '@/components/ui/RevealOnScroll';
import { AlertError, CheckCircle } from '@/components/ui/icons';
import { Toast } from '@/components/Toast';
import { trackUmamiEvent } from '@/lib/analytics';

const wrap = 'mx-auto max-w-[1120px] px-5 md:px-10';

const SubmissionSuccessMessage =
  'Your message has been received successfuly. Our team will review it and get \
  back to you as soon as possible.';

const SubmissionErrorMessage =
  "We couldn't process your request at the moment. Please check your information \
  and try again, or come back in a few minutes.";

const successToastClassName = 'border-success-fg text-success-fg bg-success';
const errorToastClassName = 'border-error-fg text-error-fg bg-error';

const toastDisplayTime = 5000;

type FlashToastProps = {
  error?: string;
};

// Keyed by the flash content in the parent, so each new flash remounts this
// component and restarts the auto-dismiss timer without setting state
// synchronously inside an effect body.
const FlashToast = ({ error }: FlashToastProps) => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShow(false), toastDisplayTime);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Toast
      message={error ? SubmissionErrorMessage : SubmissionSuccessMessage}
      icon={error ? AlertError : CheckCircle}
      show={show}
      onClick={() => setShow(false)}
      selfClosing
      className={error ? errorToastClassName : successToastClassName}
    />
  );
};

const Contact: ResolvedComponent = () => {
  const { flash } = usePage().props;
  const success = flash?.success;
  const error = flash?.error;

  return (
    <>
      <div className={wrap}>
        <PageHeader
          eyebrow="Get in touch"
          spark
          title={
            <>
              Let's plan your <em>clean</em>.
            </>
          }
          lead="Have questions or ready to book your next cleaning? Get in touch with us today — we're here to make your home sparkle. Fill out the form and our team will be happy to assist you."
        />
      </div>

      <RevealOnScroll>
        <section className="border-line border-t pt-7 pb-[52px] md:pb-20">
          <div className={wrap}>
            <div className="grid grid-cols-1 gap-9 md:grid-cols-[1fr_0.8fr] md:gap-16">
              <ContactForm />
              <div className="flex flex-col gap-6">
                <div>
                  <div className="text-ink-soft mb-1.5 text-[11.5px] font-semibold tracking-[.18em] uppercase">
                    Email
                  </div>
                  <a
                    href="mailto:hello@puretouchbydaniela.com"
                    onClick={() => trackUmamiEvent('email_click', { location: 'contact' })}
                    className="font-display text-xl"
                  >
                    hello@puretouchbydaniela.com
                  </a>
                </div>
                <div>
                  <div className="text-ink-soft mb-1.5 text-[11.5px] font-semibold tracking-[.18em] uppercase">
                    Response time
                  </div>
                  <div className="text-ink-soft text-[14.5px]">
                    We usually reply within one business day.
                  </div>
                </div>
                <div>
                  <div className="text-ink-soft mb-1.5 text-[11.5px] font-semibold tracking-[.18em] uppercase">
                    Serving
                  </div>
                  <div className="text-ink-soft text-[14.5px]">
                    Homes &amp; businesses across Toronto.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </RevealOnScroll>

      {(success || error) && <FlashToast key={`${success ?? ''}|${error ?? ''}`} error={error} />}
    </>
  );
};

export default Contact;
